import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { useCompare } from "@/context/CompareContext";
import ShareMenu from "@/components/ShareMenu";
import WhatsAppContact from "@/components/WhatsAppContact";
import { fileUrl, formatPrice } from "@/lib/api";
import { MapPin, BedDouble, Bath, Maximize, BadgeCheck, Star, Eye, GitCompareArrows } from "lucide-react";

const typeStyles = {
  sale: { label: "prop.sale", bg: "bg-brand-sale" },
  rent: { label: "prop.rent", bg: "bg-brand-rent" },
  lease: { label: "prop.lease", bg: "bg-brand-lease" },
};

export default function PropertyCard({ property }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const { feature } = useSettings();
  const { toggle, has } = useCompare();
  const ts = typeStyles[property.listing_type] || typeStyles.sale;
  const img = property.photos && property.photos.length ? fileUrl(property.photos[0]) : "";

  return (
    <div
      onClick={() => navigate(`/property/${property.id}`)}
      data-testid={`property-card-${property.id}`}
      className="group cursor-pointer bg-card border border-brand-line rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
        {img ? (
          <img src={img} alt={property.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-brand-muted">No Image</div>
        )}
        <span className={`absolute top-3 left-3 ${ts.bg} text-white text-xs font-semibold px-3 py-1 rounded-full`}>
          {t(ts.label)}
        </span>
        {feature("featured_listings") && property.featured && (
          <span className="absolute top-11 left-3 bg-amber-400 text-brand-ink text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shadow" data-testid={`featured-badge-${property.id}`}>
            <Star className="w-3 h-3 fill-current" /> Featured
          </span>
        )}
        {property.status === "sold" && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="bg-brand-terracotta text-white font-heading font-bold text-lg px-5 py-2 rounded-lg rotate-[-8deg]">{t("prop.sold")}</span>
          </div>
        )}
        <span className="absolute bottom-3 left-3 bg-white/95 backdrop-blur text-brand-ink font-heading font-bold px-3 py-1.5 rounded-lg text-sm shadow">
          {formatPrice(property.price, property.price_unit)}
        </span>
        <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
          {property.broker && property.broker.badge_number && (
            <span className="bg-brand-emerald text-white text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shadow" data-testid={`card-badge-${property.id}`}>
              <BadgeCheck className="w-3.5 h-3.5" /> {property.broker.badge_number}
            </span>
          )}
          {property.distance_km != null && (
            <span className="bg-white/90 text-brand-ink text-xs font-medium px-2.5 py-1 rounded-full">
              {property.distance_km} km
            </span>
          )}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-heading font-semibold text-brand-ink text-base leading-snug line-clamp-1">{property.title}</h3>
        <p className="flex items-center gap-1 text-sm text-brand-muted mt-1.5">
          <MapPin className="w-3.5 h-3.5 shrink-0" />
          <span className="line-clamp-1">{[property.area_name, property.city].filter(Boolean).join(", ")} {property.pincode && `- ${property.pincode}`}</span>
        </p>
        <div className="flex items-center gap-4 mt-3 text-sm text-brand-muted">
          {property.bedrooms != null && <span className="flex items-center gap-1"><BedDouble className="w-4 h-4" />{property.bedrooms}</span>}
          {property.bathrooms != null && <span className="flex items-center gap-1"><Bath className="w-4 h-4" />{property.bathrooms}</span>}
          {property.area_sqft != null && <span className="flex items-center gap-1"><Maximize className="w-4 h-4" />{property.area_sqft} sqft</span>}
          {feature("views_counter") && property.views != null && <span className="flex items-center gap-1" data-testid={`views-${property.id}`}><Eye className="w-4 h-4" />{property.views}</span>}
        </div>
        <div className="mt-3 pt-3 border-t border-brand-line flex items-center justify-between gap-2">
          {property.broker && property.broker.badge_number ? (
            <div className="flex items-center gap-1.5 text-xs text-brand-emerald font-medium line-clamp-1">
              <BadgeCheck className="w-4 h-4 shrink-0" /> {property.broker.badge_number} · {property.broker.name}
            </div>
          ) : <span />}
          <div className="flex items-center gap-1.5 shrink-0">
            <div onClick={(e) => e.stopPropagation()}>
              <WhatsAppContact property={property} variant="card" />
            </div>
            {feature("property_comparison") && (
              <button onClick={(e) => { e.stopPropagation(); toggle(property); }} data-testid={`compare-toggle-${property.id}`} title="Compare"
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${has(property.id) ? "bg-brand-emerald text-white" : "bg-secondary text-brand-muted hover:text-brand-emerald"}`}>
                <GitCompareArrows className="w-4 h-4" />
              </button>
            )}
            {feature("whatsapp_share") && (
              <div onClick={(e) => e.stopPropagation()}>
                <ShareMenu title={property.title} price={formatPrice(property.price, property.price_unit)} url={`${window.location.origin}/property/${property.id}`} testid={`share-${property.id}`} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
